import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { safeApiCall } from '../../../utils/apiHelpers';

const getDatesInRange = (start, end) => {
  const date = new Date(start);
  const endDate = new Date(end);
  const dates = [];
  while (date <= endDate) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return dates;
};

function isWeekend(date) {
  const d = new Date(date);
  return d.getDay() === 0 || d.getDay() === 6;
}

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

const ApplyLeaveModal = ({ open, onClose, onSubmit, collection, reportingManagerId }) => {
  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leaveDays, setLeaveDays] = useState([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  // When startDate changes, if endDate is empty, set endDate to startDate
  useEffect(() => {
    if (startDate && !endDate) {
      setEndDate(startDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  useEffect(() => {
    if (startDate && endDate) {
      const range = getDatesInRange(new Date(startDate), new Date(endDate));
      setLeaveDays(
        range.map(date => {
          const formatted = formatDate(date);
          const isHol = collection.holidays.some(h => h.date === formatted);
          return {
            date: formatted,
            isWeekend: isWeekend(date),
            isHoliday: isHol,
            leave_day_type: 'Full day',
          };
        })
      );
    } else {
      setLeaveDays([]);
    }
  }, [startDate, endDate, collection.holidays]);

  const handleDayTypeChange = (idx, value) => {
    setLeaveDays(prev => prev.map((d, i) => i === idx ? { ...d, leave_day_type: value } : d));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const validDates = leaveDays.filter(d => !d.isWeekend && !d.isHoliday).map(d => ({
      date: d.date,
      leave_day_type: d.leave_day_type,
    }));
    if (!leaveType || !startDate || !endDate || !reason || validDates.length === 0) {
      alert('Please fill all fields and select valid leave days.');
      setLoading(false);
      return;
    }
    try {
      await safeApiCall(onSubmit, {
        leave_type: parseInt(leaveType),
        reporting_manager: reportingManagerId,
        reason_for_leave: reason,
        dates: validDates,
      });
    } catch (err) {
      // // Show toast with backend error if present
      let msg = 'Failed to apply for leave.';
      if (err && err.error) {
        try {
          const errObj = JSON.parse(err.error);
          msg = errObj.error || msg;
        } catch {
          msg = err.error;
        }
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-lg relative">
        <h2 className="text-2xl font-bold text-center mb-4">Apply Leave</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="block font-medium mb-1">Leave Type</label>
            <select className="w-full border rounded px-2 py-1" value={leaveType} onChange={e => setLeaveType(e.target.value)} required>
              <option value="">Select Leave Type</option>
              {collection.leave_types.map(lt => (
                <option key={lt.id} value={lt.id}>{lt.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 mb-3">
            <div className="flex-1">
              <label className="block font-medium mb-1">Start Date</label>
              <input type="date" className="w-full border rounded px-2 py-1" value={startDate} onChange={e => setStartDate(e.target.value)} required />
            </div>
            <div className="flex-1">
              <label className="block font-medium mb-1">End Date</label>
              <input type="date" className="w-full border rounded px-2 py-1" value={endDate} onChange={e => setEndDate(e.target.value)} required />
            </div>
          </div>
          <div className="mb-3">
            <label className="block font-medium mb-1">Leave Days</label>
            <div className="border rounded p-2 bg-slate-50 max-h-32 overflow-y-auto">
              {leaveDays.length === 0 && <div className="text-gray-400 text-sm">Select start and end date</div>}
              {leaveDays.map((d, idx) => (
                <div key={d.date} className="flex items-center gap-2 mb-1">
                  <span className="w-28">{d.date}</span>
                  {d.isWeekend ? (
                    <span className="italic text-gray-400">Weekend</span>
                  ) : d.isHoliday ? (
                    <span className="italic text-orange-500">Holiday</span>
                  ) : (
                    <select className="border rounded px-1 py-0.5" value={d.leave_day_type} onChange={e => handleDayTypeChange(idx, e.target.value)}>
                      {collection.leave_day_type.map(ldt => (
                        <option key={ldt.id} value={ldt.id}>{ldt.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <label className="block font-medium mb-1">Reason for Leave</label>
            <textarea className="w-full border rounded px-2 py-1" rows={3} value={reason} onChange={e => setReason(e.target.value)} required placeholder="Enter your reason for leave..." />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="px-4 py-2 rounded bg-gray-200" onClick={onClose}>Cancel</button>
            <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyLeaveModal;
